import "./styles.css";

import { PitchSmoother, ToneAnalyser } from "./core/analyser.js";
import { micAvailability } from "./core/audio.js";
import { bandEnvelope, envelopePeriodHz } from "./core/dsp.js";
import {
  INTERVALS,
  beatRate,
  coincidentPartial,
  envelopeRateFor,
  intervalById,
  maxBeatRateFor,
} from "./core/intervals.js";
import { MicInput } from "./core/mic.js";
import {
  NOTE_NAMES,
  RANKS,
  centsFromNote,
  nearestNote,
  noteLabel,
  temperamentOffset,
} from "./core/pitch.js";
import { ToneEngine, WAVEFORMS } from "./core/synth.js";
import { TEMPERAMENTS } from "./core/temperaments.js";
import {
  KEYBOARD_OCTAVE_MAX,
  KEYBOARD_OCTAVE_MIN,
  OCTAVES_SHOWN,
  PITCH_PRESETS,
  SettingsStore,
  rankOf,
  soundingMidi,
  targetFrequency,
  temperamentOf,
  temperatureCentsShift,
  tuningOf,
  waveformOf,
  type Settings,
} from "./state.js";
import { Keyboard } from "./ui/keyboard.js";
import { Strobe } from "./ui/strobe.js";

/** Samples per pitch analysis. At 48 kHz this is about a third of a second. */
const ANALYSIS_SAMPLES = 16384;
/** Seconds of audio retained for beat measurement. */
const BEAT_SECONDS = 5;
const PITCH_INTERVAL_MS = 70;
const BEAT_INTERVAL_MS = 400;

function el<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`missing element #${id}`);
  return found as T;
}

const store = new SettingsStore();
const engine = new ToneEngine();
const mic = new MicInput(BEAT_SECONDS + 2);
const analyser = new ToneAnalyser(ANALYSIS_SAMPLES);
const smoother = new PitchSmoother();

const analysisBuffer = new Float32Array(ANALYSIS_SAMPLES);
let beatBuffer = new Float32Array(0);

type Mode = "play" | "tune" | "beats";
let mode: Mode = "play";
/** Note the Tune display is pinned to, or null to follow whatever is heard. */
let lockedMidi: number | null = null;
let pitchTimer = 0;
let beatTimer = 0;

// ─── Notices ────────────────────────────────────────────────────────────────

function showNotice(id: string, tone: "warn" | "info", text: string): void {
  const host = el("notices");
  let notice = document.getElementById(`notice-${id}`);
  if (!notice) {
    notice = document.createElement("p");
    notice.id = `notice-${id}`;
    host.append(notice);
  }
  notice.className = `notice notice-${tone}`;
  notice.textContent = text;
}

function clearNotice(id: string): void {
  document.getElementById(`notice-${id}`)?.remove();
}

// ─── Settings drawer ────────────────────────────────────────────────────────

function populateSelects(): void {
  const temperament = el<HTMLSelectElement>("temperament");
  for (const item of TEMPERAMENTS) {
    const option = document.createElement("option");
    option.value = item.id;
    option.textContent = item.year === "modern" ? item.name : `${item.name} · ${item.year}`;
    temperament.append(option);
  }

  const rank = el<HTMLSelectElement>("rank");
  for (const item of RANKS) {
    const option = document.createElement("option");
    option.value = item.id;
    option.textContent = `${item.label} — ${item.note}`;
    rank.append(option);
  }

  const waveform = el<HTMLSelectElement>("waveform");
  for (const item of WAVEFORMS) {
    const option = document.createElement("option");
    option.value = item.id;
    option.textContent = item.name;
    waveform.append(option);
  }

  const presets = el("pitch-presets");
  for (const preset of PITCH_PRESETS) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "chip";
    chip.dataset.hz = String(preset.hz);
    chip.textContent = preset.label;
    chip.addEventListener("click", () => store.update({ referenceHz: preset.hz }));
    presets.append(chip);
  }

  const root = el<HTMLSelectElement>("beat-root");
  for (let midi = 36; midi <= 84; midi++) {
    const option = document.createElement("option");
    option.value = String(midi);
    option.textContent = noteLabel(midi);
    root.append(option);
  }

  const interval = el<HTMLSelectElement>("beat-interval");
  for (const item of INTERVALS) {
    const option = document.createElement("option");
    option.value = item.id;
    option.textContent = `${item.name} (${item.numerator}:${item.denominator})`;
    interval.append(option);
  }
}

/** Draw the temperament as a row of cents-from-equal bars. */
function renderTemperamentMap(settings: Settings): void {
  const host = el("temperament-map");
  const tuning = tuningOf(settings);
  const offsets = NOTE_NAMES.map((_, pitchClass) => temperamentOffset(60 + pitchClass, tuning));
  const extent = Math.max(6, ...offsets.map(Math.abs));

  host.replaceChildren();
  NOTE_NAMES.forEach((name, pitchClass) => {
    const cents = offsets[pitchClass]!;
    const cell = document.createElement("div");
    cell.className = "map-cell";

    const bar = document.createElement("div");
    bar.className = "map-bar";
    const magnitude = (Math.abs(cents) / extent) * 50;
    if (cents >= 0) {
      bar.style.bottom = "50%";
      bar.style.height = `${magnitude}%`;
      bar.classList.add("is-sharp");
    } else {
      bar.style.top = "50%";
      bar.style.height = `${magnitude}%`;
      bar.classList.add("is-flat");
    }

    const label = document.createElement("span");
    label.className = "map-label";
    label.textContent = name;

    const value = document.createElement("span");
    value.className = "map-value";
    value.textContent = cents === 0 ? "0" : cents.toFixed(1);

    cell.append(bar, label, value);
    cell.title = `${name}: ${cents >= 0 ? "+" : ""}${cents.toFixed(2)} cents from equal`;
    host.append(cell);
  });
}

function syncSettingsUi(settings: Settings): void {
  el<HTMLInputElement>("reference-hz").value = settings.referenceHz.toFixed(1);
  el<HTMLSelectElement>("temperament").value = settings.temperamentId;
  el<HTMLSelectElement>("rank").value = settings.rankId;
  el<HTMLSelectElement>("waveform").value = settings.waveformId;
  el<HTMLInputElement>("volume").value = String(Math.round(settings.volume * 100));
  el<HTMLInputElement>("temp-enabled").checked = settings.temperatureEnabled;
  el<HTMLInputElement>("temp-now").value = String(settings.temperatureNowC);
  el<HTMLInputElement>("temp-target").value = String(settings.temperatureTargetC);
  el<HTMLSelectElement>("beat-root").value = String(settings.beatRootMidi);
  el<HTMLSelectElement>("beat-interval").value = settings.beatIntervalId;

  for (const chip of document.querySelectorAll<HTMLElement>("#pitch-presets .chip")) {
    chip.classList.toggle("is-active", Number(chip.dataset.hz) === settings.referenceHz);
  }

  el("temperament-note").textContent = temperamentOf(settings).description;
  el("waveform-note").textContent = waveformOf(settings).description;

  const rank = rankOf(settings);
  el("rank-note").textContent =
    rank.semitones === 0
      ? "Keys sound at written pitch."
      : `Keys sound ${Math.abs(rank.semitones)} semitones ${
          rank.semitones > 0 ? "above" : "below"
        } written pitch — press the key, tune the pipe it opens.`;

  el("temp-fields").classList.toggle("is-disabled", !settings.temperatureEnabled);
  const shift = temperatureCentsShift(settings);
  el("temp-note").textContent = settings.temperatureEnabled
    ? shift === 0
      ? "Both temperatures the same, so nothing is being shifted."
      : `Every target is shifted ${shift > 0 ? "up" : "down"} ${Math.abs(shift).toFixed(
          1,
        )} cents, so the flue pipes land in tune once the building reaches ${
          settings.temperatureTargetC
        } °C. Reeds barely move with temperature — do not compensate them.`
    : "Flue pipes sharpen about 3 cents per degree as the building warms. Turn this on to tune now for the temperature you expect later.";

  renderTemperamentMap(settings);

  const temperament = temperamentOf(settings);
  el("topbar-pitch").textContent = `A${settings.referenceHz.toFixed(
    settings.referenceHz % 1 === 0 ? 0 : 1,
  )} · ${temperament.name}`;
}

function bindSettings(): void {
  const open = () => {
    el("settings").hidden = false;
    el("scrim").hidden = false;
    el("settings-open").setAttribute("aria-expanded", "true");
  };
  const close = () => {
    el("settings").hidden = true;
    el("scrim").hidden = true;
    el("settings-open").setAttribute("aria-expanded", "false");
  };
  el("settings-open").addEventListener("click", open);
  el("settings-close").addEventListener("click", close);
  el("scrim").addEventListener("click", close);
  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape") close();
  });

  const referenceInput = el<HTMLInputElement>("reference-hz");
  referenceInput.addEventListener("change", () => {
    const value = Number(referenceInput.value);
    if (Number.isFinite(value)) store.update({ referenceHz: value });
    else syncSettingsUi(store.current);
  });
  el("pitch-down").addEventListener("click", () =>
    store.update({ referenceHz: Math.round((store.current.referenceHz - 0.5) * 10) / 10 }),
  );
  el("pitch-up").addEventListener("click", () =>
    store.update({ referenceHz: Math.round((store.current.referenceHz + 0.5) * 10) / 10 }),
  );

  el<HTMLSelectElement>("temperament").addEventListener("change", (event) =>
    store.update({ temperamentId: (event.target as HTMLSelectElement).value }),
  );
  el<HTMLSelectElement>("rank").addEventListener("change", (event) =>
    store.update({ rankId: (event.target as HTMLSelectElement).value }),
  );
  el<HTMLSelectElement>("waveform").addEventListener("change", (event) =>
    store.update({ waveformId: (event.target as HTMLSelectElement).value }),
  );

  el<HTMLInputElement>("temp-enabled").addEventListener("change", (event) =>
    store.update({ temperatureEnabled: (event.target as HTMLInputElement).checked }),
  );
  for (const [id, key] of [
    ["temp-now", "temperatureNowC"],
    ["temp-target", "temperatureTargetC"],
  ] as const) {
    const input = el<HTMLInputElement>(id);
    input.addEventListener("change", () => {
      const value = Number(input.value);
      if (Number.isFinite(value)) store.update({ [key]: value } as Partial<Settings>);
      else syncSettingsUi(store.current);
    });
  }
}

// ─── Mode 1: reference tones ────────────────────────────────────────────────

const keyboard = new Keyboard(el("keyboard"), {
  onPress: (midi) => void pressKey(midi),
  onRelease: (midi) => releaseKey(midi),
});

let lastPressed: number | null = null;

function voiceId(keyMidi: number): string {
  return `key:${keyMidi}`;
}

async function pressKey(keyMidi: number): Promise<void> {
  await engine.unlock();
  const settings = store.current;
  const id = voiceId(keyMidi);

  if (settings.hold && engine.isSounding(id)) {
    engine.noteOff(id);
    refreshPlayReadout();
    return;
  }

  lastPressed = keyMidi;
  engine.noteOn(id, targetFrequency(soundingMidi(keyMidi, settings), settings));
  refreshPlayReadout();
}

function releaseKey(keyMidi: number): void {
  if (store.current.hold) return;
  engine.noteOff(voiceId(keyMidi));
  refreshPlayReadout();
}

function soundingKeyMidis(): number[] {
  return engine
    .soundingIds()
    .filter((id) => id.startsWith("key:"))
    .map((id) => Number(id.slice(4)))
    .filter((midi) => Number.isFinite(midi));
}

function refreshPlayReadout(): void {
  const settings = store.current;
  const sounding = soundingKeyMidis();
  keyboard.setSounding(sounding);

  const shown = lastPressed !== null && engine.isSounding(voiceId(lastPressed))
    ? lastPressed
    : (sounding[sounding.length - 1] ?? null);

  if (shown === null) {
    el("play-note").textContent = "—";
    el("play-hz").textContent = sounding.length ? "" : "press a key";
    el("play-sub").textContent = "";
    return;
  }

  const sounded = soundingMidi(shown, settings);
  const hz = targetFrequency(sounded, settings);
  const rank = rankOf(settings);

  el("play-note").textContent = noteLabel(sounded);
  el("play-hz").textContent = `${hz.toFixed(2)} Hz`;

  const parts: string[] = [];
  if (rank.semitones !== 0) parts.push(`key ${noteLabel(shown)} on ${rank.label}`);
  const offset = temperamentOffset(sounded, tuningOf(settings));
  if (Math.abs(offset) > 0.005) {
    parts.push(`${offset > 0 ? "+" : ""}${offset.toFixed(1)} ¢ from equal`);
  }
  if (sounding.length > 1) parts.push(`${sounding.length} notes sounding`);
  el("play-sub").textContent = parts.join(" · ");
}

/** Re-tune every sounding voice after a settings change. */
function retuneSoundingVoices(settings: Settings): void {
  for (const keyMidi of soundingKeyMidis()) {
    engine.setFrequency(voiceId(keyMidi), targetFrequency(soundingMidi(keyMidi, settings), settings));
  }
  if (engine.isSounding("beat:lower")) {
    engine.setFrequency("beat:lower", beatLowerFrequency(settings));
  }
}

/** Octave range currently built into the DOM, so it is only rebuilt when it moves. */
let renderedOctave: number | null = null;

function renderKeyboard(settings: Settings): void {
  // Rebuilding is destructive: it replaces every key element and drops any
  // pointer holding one down. Volume drags fire a settings change per pixel, so
  // only rebuild when the range on screen has actually changed.
  if (renderedOctave !== settings.keyboardOctave) {
    keyboard.render(settings.keyboardOctave, OCTAVES_SHOWN);
    renderedOctave = settings.keyboardOctave;
  }
  keyboard.setSounding(soundingKeyMidis());
  const low = keyboard.lowestMidi;
  const high = keyboard.highestMidi;
  el("keyboard-range").textContent = low !== null && high !== null
    ? `${noteLabel(low)} – ${noteLabel(high)}`
    : "";
  el<HTMLButtonElement>("octave-down").disabled = settings.keyboardOctave <= KEYBOARD_OCTAVE_MIN;
  el<HTMLButtonElement>("octave-up").disabled = settings.keyboardOctave >= KEYBOARD_OCTAVE_MAX;
}

function bindPlayMode(): void {
  const holdToggle = el("hold-toggle");
  holdToggle.addEventListener("click", () => {
    const hold = !store.current.hold;
    store.update({ hold });
    // Leaving hold should not strand notes that were latched on.
    if (!hold) {
      for (const keyMidi of soundingKeyMidis()) engine.noteOff(voiceId(keyMidi));
      refreshPlayReadout();
    }
  });

  el("all-off").addEventListener("click", () => {
    engine.allOff();
    el<HTMLElement>("beat-sound").setAttribute("aria-pressed", "false");
    refreshPlayReadout();
  });

  const volume = el<HTMLInputElement>("volume");
  volume.addEventListener("input", () => store.update({ volume: Number(volume.value) / 100 }));

  el("octave-down").addEventListener("click", () =>
    store.update({ keyboardOctave: store.current.keyboardOctave - 1 }),
  );
  el("octave-up").addEventListener("click", () =>
    store.update({ keyboardOctave: store.current.keyboardOctave + 1 }),
  );
}

// ─── Mode 2: listen and measure ─────────────────────────────────────────────

const strobe = new Strobe(el<HTMLCanvasElement>("strobe"));

function setNeedle(cents: number | null): void {
  const needle = el("meter-needle");
  if (cents === null) {
    needle.style.left = "50%";
    needle.classList.remove("is-close", "is-live");
    return;
  }
  const clamped = Math.max(-50, Math.min(50, cents));
  needle.style.left = `${50 + clamped}%`;
  needle.classList.add("is-live");
  needle.classList.toggle("is-close", Math.abs(cents) <= 2);
}

function renderMeterScale(): void {
  const scale = el("meter-scale");
  scale.replaceChildren();
  for (let cents = -50; cents <= 50; cents += 10) {
    const tick = document.createElement("span");
    tick.className = cents === 0 ? "tick tick-zero" : "tick";
    tick.style.left = `${50 + cents}%`;
    if (cents % 20 === 0 && cents !== 0) tick.dataset.label = String(cents);
    scale.append(tick);
  }
}

function clearTuneReadout(message: string): void {
  el("tune-note").textContent = "—";
  el("tune-cents").textContent = "—";
  el("tune-hz").textContent = message;
  el("meter-state").textContent = "—";
  setNeedle(null);
  strobe.setDeviation(null);
}

function updateLevel(levelDb: number | null): void {
  const fill = el("level-fill");
  const value = el("level-value");
  if (levelDb === null) {
    fill.style.width = "0%";
    value.textContent = "—";
    return;
  }
  // -60 dBFS is the detection floor, 0 is clipping.
  const fraction = Math.max(0, Math.min(1, (levelDb + 60) / 60));
  fill.style.width = `${fraction * 100}%`;
  fill.classList.toggle("is-hot", levelDb > -6);
  value.textContent = `${levelDb.toFixed(0)} dB`;
}

function analysePitch(): void {
  if (!mic.readLatest(analysisBuffer)) {
    clearTuneReadout("listening…");
    updateLevel(null);
    return;
  }

  const settings = store.current;
  analyser.configure({ guardSubOctave: lockedMidi === null });
  const reading = analyser.analyse(analysisBuffer, mic.sampleRate);

  if (!reading) {
    smoother.clear();
    clearTuneReadout("no steady tone");
    updateLevel(null);
    return;
  }

  updateLevel(reading.levelDb);
  smoother.push(reading.frequency);
  const smoothed = smoother.read();
  if (!smoothed) return;

  const tuning = tuningOf(settings);
  const adjustment = temperatureCentsShift(settings);

  let midi: number;
  let cents: number;
  if (lockedMidi !== null) {
    midi = lockedMidi;
    cents = centsFromNote(smoothed.frequency, midi, tuning) - adjustment;
  } else {
    const match = nearestNote(smoothed.frequency, tuning);
    if (!match) return;
    midi = match.midi;
    cents = match.cents - adjustment;
  }

  el("tune-note").textContent = noteLabel(midi);
  el("tune-cents").textContent = `${cents >= 0 ? "+" : "−"}${Math.abs(cents).toFixed(1)} ¢`;
  el("tune-cents").className = `readout-cents ${
    Math.abs(cents) <= 1 ? "is-good" : Math.abs(cents) <= 5 ? "is-near" : "is-off"
  }`;

  const rank = rankOf(settings);
  const keyNote = rank.semitones === 0 ? "" : ` · key ${noteLabel(midi - rank.semitones)} on ${rank.label}`;
  el("tune-hz").textContent = `${smoothed.frequency.toFixed(2)} Hz · target ${targetFrequency(
    midi,
    settings,
  ).toFixed(2)} Hz${keyNote}`;

  el("meter-state").textContent = smoothed.settled
    ? Math.abs(cents) <= 1
      ? "in tune"
      : cents > 0
        ? "sharp — flatten"
        : "flat — sharpen"
    : `settling (±${smoothed.spreadCents.toFixed(1)} ¢)`;

  setNeedle(cents);
  strobe.setDeviation(cents);
  el<HTMLElement>("lock-label").textContent =
    lockedMidi === null ? "Lock note" : `Locked to ${noteLabel(lockedMidi)}`;
}

function bindTuneMode(): void {
  el("tune-start").addEventListener("click", () => void startListening("tune"));

  el("lock-toggle").addEventListener("click", () => {
    if (lockedMidi !== null) {
      lockedMidi = null;
    } else {
      const smoothed = smoother.read();
      const match = smoothed ? nearestNote(smoothed.frequency, tuningOf(store.current)) : null;
      // Nothing being heard yet, so there is nothing to lock on to.
      if (!match) {
        showNotice("lock", "info", "Play the pipe first, then lock the note it lands on.");
        window.setTimeout(() => clearNotice("lock"), 4000);
        return;
      }
      lockedMidi = match.midi;
    }
    el("lock-toggle").setAttribute("aria-pressed", String(lockedMidi !== null));
    el("lock-label").textContent =
      lockedMidi === null ? "Lock note" : `Locked to ${noteLabel(lockedMidi)}`;
    smoother.clear();
  });
}

// ─── Mode 3: beat counting ──────────────────────────────────────────────────

function beatLowerFrequency(settings: Settings): number {
  return targetFrequency(settings.beatRootMidi, settings);
}

function beatPair(settings: Settings): { lower: number; upper: number; lowerHz: number; upperHz: number } {
  const interval = intervalById(settings.beatIntervalId);
  const lower = settings.beatRootMidi;
  const upper = lower + interval.semitones;
  return {
    lower,
    upper,
    lowerHz: targetFrequency(lower, settings),
    upperHz: targetFrequency(upper, settings),
  };
}

function refreshBeatTarget(): void {
  const settings = store.current;
  const interval = intervalById(settings.beatIntervalId);
  const { lower, upper, lowerHz, upperHz } = beatPair(settings);
  const target = beatRate(lowerHz, upperHz, interval);

  el("beat-target").textContent =
    interval.semitones === 0
      ? "A unison should not beat at all."
      : `${temperamentOf(settings).name} wants ${target.toFixed(2)} beats/sec here.`;

  el("beat-pair").textContent = `${noteLabel(lower)} ${lowerHz.toFixed(2)} Hz + ${noteLabel(
    upper,
  )} ${upperHz.toFixed(2)} Hz · listening at ${coincidentPartial(lowerHz, interval).toFixed(
    0,
  )} Hz`;
}

function measureBeats(): void {
  const settings = store.current;
  const interval = intervalById(settings.beatIntervalId);
  const { lowerHz, upperHz } = beatPair(settings);

  const wanted = Math.min(Math.floor(BEAT_SECONDS * mic.sampleRate), mic.available);
  if (wanted < mic.sampleRate) {
    el("beat-rate").textContent = "—";
    return;
  }
  if (beatBuffer.length !== wanted) beatBuffer = new Float32Array(wanted);
  if (!mic.readLatest(beatBuffer)) return;

  const envelopeRate = envelopeRateFor(lowerHz);
  const envelope = bandEnvelope(
    beatBuffer,
    mic.sampleRate,
    coincidentPartial(lowerHz, interval),
    envelopeRate,
  );
  const found = envelopePeriodHz(envelope, envelopeRate, 0.25, maxBeatRateFor(envelopeRate));

  const pulse = el("beat-pulse");
  if (!found) {
    el("beat-rate").textContent = "—";
    el("beats-footnote").textContent =
      "No steady beating heard. Sound both notes together and hold the phone near the pipe mouths.";
    pulse.style.removeProperty("--beat-period");
    pulse.classList.remove("is-beating");
    return;
  }

  const target = beatRate(lowerHz, upperHz, interval);
  el("beat-rate").textContent = found.hz.toFixed(2);
  pulse.style.setProperty("--beat-period", `${(1 / found.hz).toFixed(3)}s`);
  pulse.classList.add("is-beating");

  const difference = found.hz - target;
  const verdict =
    Math.abs(difference) < 0.15
      ? "Matches the temperament."
      : difference > 0
        ? `Beating ${difference.toFixed(2)}/sec too fast — the interval is too far from just.`
        : `Beating ${Math.abs(difference).toFixed(2)}/sec too slow — the interval is too close to just.`;

  el("beats-footnote").textContent =
    interval.semitones === 0
      ? `${verdict} Slow the beating to nothing.`
      : `${verdict} Move the upper pipe and watch which way the rate goes; if it speeds up, go back the other way.`;
}

function bindBeatsMode(): void {
  el("beats-start").addEventListener("click", () => void startListening("beats"));

  el<HTMLSelectElement>("beat-root").addEventListener("change", (event) =>
    store.update({ beatRootMidi: Number((event.target as HTMLSelectElement).value) }),
  );
  el<HTMLSelectElement>("beat-interval").addEventListener("change", (event) =>
    store.update({ beatIntervalId: (event.target as HTMLSelectElement).value }),
  );

  el("beat-sound").addEventListener("click", async () => {
    await engine.unlock();
    const button = el("beat-sound");
    if (engine.isSounding("beat:lower")) {
      engine.noteOff("beat:lower");
      button.setAttribute("aria-pressed", "false");
    } else {
      engine.noteOn("beat:lower", beatLowerFrequency(store.current));
      button.setAttribute("aria-pressed", "true");
    }
  });
}

// ─── Microphone lifecycle ───────────────────────────────────────────────────

async function startListening(which: "tune" | "beats"): Promise<void> {
  const availability = micAvailability();
  if (!availability.ok) {
    showNotice("mic", "warn", availability.reason);
    return;
  }

  try {
    await mic.start();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    showNotice(
      "mic",
      "warn",
      message.includes("Permission") || message.includes("denied")
        ? "Microphone permission was refused. Allow it in your browser's site settings and try again."
        : `Could not open the microphone: ${message}`,
    );
    return;
  }

  clearNotice("mic");
  el(which === "tune" ? "tune-gate" : "beats-gate").hidden = true;
  el(which === "tune" ? "tune-body" : "beats-body").hidden = false;
  syncLoops();
}

/** Run only the analysis the visible mode needs. */
function syncLoops(): void {
  window.clearInterval(pitchTimer);
  window.clearInterval(beatTimer);
  pitchTimer = 0;
  beatTimer = 0;
  strobe.stop();

  if (!mic.running) return;

  if (mode === "tune" && !el("tune-body").hidden) {
    strobe.resize();
    strobe.start();
    pitchTimer = window.setInterval(analysePitch, PITCH_INTERVAL_MS);
  } else if (mode === "beats" && !el("beats-body").hidden) {
    beatTimer = window.setInterval(measureBeats, BEAT_INTERVAL_MS);
  }
}

// ─── Modes ──────────────────────────────────────────────────────────────────

function setMode(next: Mode): void {
  mode = next;
  for (const tab of document.querySelectorAll<HTMLElement>(".mode-tab")) {
    const active = tab.dataset.mode === next;
    tab.classList.toggle("is-active", active);
    tab.setAttribute("aria-selected", String(active));
  }
  for (const panel of document.querySelectorAll<HTMLElement>(".panel")) {
    panel.classList.toggle("is-active", panel.id === `panel-${next}`);
  }
  smoother.clear();
  syncLoops();
  if (next === "beats") refreshBeatTarget();
}

function bindModes(): void {
  for (const tab of document.querySelectorAll<HTMLElement>(".mode-tab")) {
    tab.addEventListener("click", () => setMode(tab.dataset.mode as Mode));
  }
}

// ─── Boot ───────────────────────────────────────────────────────────────────

function applySettings(settings: Settings): void {
  engine.setWaveform(settings.waveformId);
  engine.setVolume(settings.volume);
  syncSettingsUi(settings);
  renderKeyboard(settings);
  retuneSoundingVoices(settings);
  refreshPlayReadout();
  refreshBeatTarget();

  el("hold-toggle").setAttribute("aria-pressed", String(settings.hold));
  el("play-footnote").textContent = settings.hold
    ? "Hold is on: tap a key to start it, tap again to stop. Tap several to compare intervals."
    : "Notes sound while held. Turn on Hold to leave one sounding with both hands free.";

  const shift = temperatureCentsShift(settings);
  if (settings.temperatureEnabled && Math.abs(shift) >= 0.05) {
    showNotice(
      "temperature",
      "info",
      `Temperature compensation is on: every target is ${Math.abs(shift).toFixed(1)} cents ${
        shift > 0 ? "sharp" : "flat"
      } of nominal, for ${settings.temperatureNowC} °C now and ${
        settings.temperatureTargetC
      } °C later.`,
    );
  } else {
    clearNotice("temperature");
  }
}

function boot(): void {
  populateSelects();
  renderMeterScale();
  bindSettings();
  bindPlayMode();
  bindTuneMode();
  bindBeatsMode();
  bindModes();

  store.subscribe(applySettings);
  applySettings(store.current);

  const availability = micAvailability();
  if (!availability.ok) {
    showNotice("mic", "warn", availability.reason);
    for (const id of ["tune-start", "beats-start"]) {
      el<HTMLButtonElement>(id).disabled = true;
    }
  }

  el("tune-footnote").textContent =
    "Pitch is read from a third of a second of audio and refined by phase, so a settled reading is good to well under a tenth of a cent. Wait for it to settle before touching the pipe.";

  window.addEventListener("resize", () => strobe.resize());
  document.addEventListener("visibilitychange", () => {
    // A tuner left in a pocket should not keep the microphone and CPU busy.
    if (document.hidden) {
      window.clearInterval(pitchTimer);
      window.clearInterval(beatTimer);
      strobe.stop();
    } else {
      syncLoops();
    }
  });

  setMode("play");
}

boot();
